import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

/**
 * Centrum kosztowe jako ENCJA (`UserCostCenters`).
 *
 * ⚠️ SPS trzyma centrum kosztowe JEDNOCZEŚNIE w dwóch postaciach i obie stempluje na wydaniu:
 * goły napis `Users.COSTCENTER` → `TransactionLog.COSTCENTER` oraz przynależność do encji
 * → `TransactionLog.USERCOSTCENTER`. Dlatego `members` i `onUsersColumn` MOGĄ się różnić,
 * i wtedy dwa raporty „z SPS" dają dwie różne odpowiedzi na to samo pytanie.
 */
export const CostCenterRowSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  alias: z.string().nullable(),
  /** Ilu pracowników ma PRZYNALEŻNOŚĆ (`UserCostCenterMembership`). */
  members: z.number(),
  /** Ilu pracowników ma ten kod w gołym polu `Users.COSTCENTER`. */
  onUsersColumn: z.number(),
  pending: z.boolean(),
});

export type CostCenterRow = z.infer<typeof CostCenterRowSchema>;

/**
 * KOD Z KART SPOZA SŁOWNIKA (13.10.2026) — centrum wpisane u ludzi w `Users.COSTCENTER`, bez wpisu
 * w `UserCostCenters`. Na TYCO 61 takich kodów u 1319 osób przy pustym słowniku.
 */
export const KodZKartSchema = z.object({ code: z.string(), osob: z.number() });
export type KodZKart = z.infer<typeof KodZKartSchema>;

const CostCentersSchema = z.object({
  rows: z.array(CostCenterRowSchema),
  /** Starsze API tego nie zna — pusta lista, a nie błąd całego ekranu. */
  spozaSlownika: z.array(KodZKartSchema).default([]),
  summary: z.object({
    centers: z.number(),
    /** Napis w `Users` wskazuje INNE centrum niż przynależność. */
    mismatch: z.number(),
    /** Pracownik w WIĘCEJ niż jednym centrum — goły napis zmieści tylko jedno. */
    multiple: z.number(),
    onlyColumn: z.number(),
    onlyMembership: z.number(),
    /** Kody z `Users.COSTCENTER`, których encja nie zna — ślad literówki w wolnym polu. */
    unknownCodes: z.number(),
    /** Ilu w alarmie — sam napis bez przynależności się nie liczy (13.10.2026). Brak = starsze API. */
    doPoprawy: z.number().optional(),
  }),
});

export const listCostCenters = (groupId: string, search?: string) => {
  const q = new URLSearchParams({ groupId });
  if (search) q.set('search', search);
  return apiGet(`/cost-centers?${q.toString()}`, CostCentersSchema);
};

const OpSchema = z.object({ opId: z.string() });

/** Nowe centrum w słowniku — klucz `UserCostCenters` potwierdzony 29.09.2026, bez dawnego potwierdzenia. */
export const createCostCenter = (groupId: string, code: string, name: string, alias?: string) =>
  apiSend(
    'POST',
    '/cost-centers',
    { groupId, code, name, ...(alias ? { alias } : {}) },
    OpSchema.extend({ id: z.string() }),
  );

/**
 * Kody z kart do słownika — hurtem, jedną operacją, BEZ przynależności: automat liczy przynależności
 * osoby, a sam wpis w słowniku nikomu jej nie daje, więc na maszynie nic się nie zmienia.
 */
export const dodajDoSlownika = (groupId: string, pozycje: { code: string; name: string }[]) =>
  apiSend('POST', '/cost-centers/do-slownika', { groupId, pozycje }, OpSchema.extend({ dodanych: z.number() }));

/**
 * USUNIĘCIE CENTRUM (03.11.2026) — przynależności, `Users.COSTCENTER` i słownik naraz. Po KODZIE,
 * bo kod „z kart" nie ma identyfikatora. Najpierw skutki do okna, potem samo usunięcie.
 */
export const SkutkiUsunieciaSchema = z.object({
  kod: z.string(),
  wSlowniku: z.object({ id: z.string(), name: z.string() }).nullable(),
  przynaleznosci: z.array(z.string()),
  napisy: z.array(z.string()),
  wKolejce: z.boolean(),
  harmonogramy: z.array(z.object({ id: z.string(), nazwa: z.string() })),
  hrPlus: z.object({ wlaczona: z.boolean(), zakladajMpk: z.boolean() }),
});

export const fetchSkutkiUsunieciaCentrum = (groupId: string, kod: string) =>
  apiGet(`/cost-centers/usuniecie?groupId=${encodeURIComponent(groupId)}&kod=${encodeURIComponent(kod)}`, SkutkiUsunieciaSchema);

export const usunCentrum = (groupId: string, kod: string) =>
  apiSend('POST', '/cost-centers/usun', { groupId, kod }, z.object({ opIds: z.array(z.string()) }));

/**
 * Przypisanie pracownika — jedna operacja ruszająca `UserCostCenterMembership` **i**
 * `Users.COSTCENTER`. `null` zdejmuje obie postacie.
 */
export const assignCostCenter = (employeeId: string, groupId: string, costCenterId: string | null) =>
  apiSend('POST', `/cost-centers/employees/${employeeId}`, { groupId, costCenterId }, OpSchema);

/**
 * KTO MA NIEZGODNE CENTRUM KOSZTOWE.
 *
 * ⚠️ ZGŁOSZONE 01.09.2026: alarm podawał liczbę i kazał „poprawić w tabeli poniżej", a tabela na tym
 * ekranie jest listą CENTRÓW, nie ludzi. Bez tej listy nie dało się po tych ludziach przejść.
 */
export const NiezgodniSchema = z.object({
  wszystkich: z.number(),
  rows: z.array(
    z.object({
      id: z.string(),
      karta: z.string(),
      name: z.string(),
      napis: z.string().nullable(),
      przynaleznosc: z.array(z.string()),
      rodzaj: z.enum(['MISMATCH', 'ONLY_COLUMN', 'ONLY_MEMBERSHIP']).nullable(),
      wielu: z.boolean(),
    }),
  ),
});

export const fetchNiezgodneCentra = (groupId: string) =>
  apiGet(`/cost-centers/niezgodni?groupId=${encodeURIComponent(groupId)}`, NiezgodniSchema);
